import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import { formatLocale } from '@plerionhq/design-system';

const BASE_PATH = '/api/core/pleri/roi-reports';

/**
 * Dashboard schema shape (same as ManageDashboard / UniversalDashboardRenderer).
 * Optional on each report; when present, render with the dashboard component.
 */
export interface RoiReportDashboardSchema {
  title?: string;
  titleIcon?: string;
  description?: string;
  sections?: Array<{
    title?: string;
    description?: string;
    sectionDescription?: string;
    sectionFooter?: string;
    widgets?: unknown[];
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

/** Generation status for async ROI report creation. */
export type RoiReportGenerationStatus = 'in_progress' | 'completed';

/** One month's ROI report (list item or detail). */
export interface PleriRoiReport {
  tenantId: string;
  organizationId: string;
  month: string;
  generatedAt: string;
  summary?: string | null;
  report?: string | null;
  /** When present, the Engagement & ROI dashboard for this month. */
  dashboardSchema?: RoiReportDashboardSchema | null;
  /** Present when report is being generated; omitted or 'completed' when done. */
  generationStatus?: RoiReportGenerationStatus;
  /** Set when generation starts (in_progress). */
  generationStartedAt?: string | null;
}

/** Error code returned when generate is called while already in progress. */
export const ROI_GENERATION_IN_PROGRESS = 'ROI_GENERATION_IN_PROGRESS';

/** Thrown when POST generate returns 409. */
export class RoiGenerationInProgressError extends Error {
  code = ROI_GENERATION_IN_PROGRESS;
  constructor(message: string) {
    super(message);
    this.name = 'RoiGenerationInProgressError';
  }
}

/** List API response. */
export interface ListRoiReportsResponse {
  data: { reports: PleriRoiReport[] };
  meta: { total: number };
}

/** Generate API request. */
export interface GenerateRoiReportRequest {
  month?: string;
}

/** Generate API response (202). */
export interface GenerateRoiReportResponse {
  message: string;
  month: string;
  tenantId: string;
}

export interface ListRoiReportsParams {
  month?: string;
  limit?: number;
}

/** Earliest month we support for ROI reports (Feb 2026). */
export const ROI_REPORTS_MIN_MONTH = '2026-02';

/** Number of months to show in pickers (from min month through last month). */
const ROI_REPORTS_MONTH_WINDOW = 6;

/**
 * Returns supported month options for ROI reports: from Feb 2026 up to last month,
 * at most 6 months (most recent first).
 */
export function getSupportedRoiMonthOptions(): {
  label: string;
  value: string;
}[] {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const minDate = new Date(2026, 1, 1); // Feb 2026
  if (lastMonth < minDate) {
    return [{ label: 'February 2026', value: '2026-02' }];
  }
  const options: { label: string; value: string }[] = [];
  let cursor = new Date(lastMonth.getFullYear(), lastMonth.getMonth(), 1);
  while (options.length < ROI_REPORTS_MONTH_WINDOW && cursor >= minDate) {
    const y = cursor.getFullYear();
    const m = cursor.getMonth() + 1;
    const value = `${y}-${String(m).padStart(2, '0')}`;
    const label = cursor.toLocaleDateString(formatLocale(), {
      month: 'long',
      year: 'numeric',
    });
    options.push({ label, value });
    cursor = new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1);
  }
  return options;
}

/** Default month for generate (last month, or Feb 2026 if we're before that). */
export function getDefaultRoiReportMonth(): string {
  const opts = getSupportedRoiMonthOptions();
  return opts[0]?.value ?? ROI_REPORTS_MIN_MONTH;
}

export async function fetchListRoiReports(
  params: ListRoiReportsParams = {},
): Promise<ListRoiReportsResponse> {
  const query = new URLSearchParams();
  if (params.month) {
    query.set('month', params.month);
  }
  if (params.limit != null && params.limit > 0) {
    query.set('limit', String(Math.min(100, Math.max(1, params.limit))));
  }
  const url =
    query.toString() === '' ? BASE_PATH : `${BASE_PATH}?${query.toString()}`;
  const { data } = await api.get<ListRoiReportsResponse>(url);
  return data;
}

export async function fetchRoiReportByMonth(
  month: string,
): Promise<PleriRoiReport | null> {
  try {
    const { data } = await api.get<{ data: PleriRoiReport }>(
      `${BASE_PATH}/${month}`,
    );
    return data.data;
  } catch (err) {
    const axiosErr = err as { response?: { status?: number } };
    if (axiosErr.response?.status === 404) {
      // Fallback: list with month & limit=1
      const list = await fetchListRoiReports({ month, limit: 1 });
      const first = list.data?.reports?.[0];
      return first ?? null;
    }
    throw err;
  }
}

export async function generateRoiReport(
  body: GenerateRoiReportRequest = {},
): Promise<GenerateRoiReportResponse> {
  try {
    const { data, status } = await api.post<GenerateRoiReportResponse>(
      `${BASE_PATH}/generate`,
      body,
      { validateStatus: (s) => s === 202 || s === 200 || s === 409 },
    );
    if (status === 409) {
      const errBody = data as { code?: string; message?: string };
      throw new RoiGenerationInProgressError(
        typeof errBody?.message === 'string'
          ? errBody.message
          : 'Report generation is already in progress for this month.',
      );
    }
    return data as GenerateRoiReportResponse;
  } catch (err) {
    if (err instanceof RoiGenerationInProgressError) {
      throw err;
    }
    const axiosErr = err as { response?: { status?: number; data?: unknown } };
    if (axiosErr.response?.status === 409) {
      const body = axiosErr.response?.data;
      const message =
        body &&
        typeof body === 'object' &&
        'message' in body &&
        typeof (body as { message: unknown }).message === 'string'
          ? (body as { message: string }).message
          : 'Report generation is already in progress for this month.';
      throw new RoiGenerationInProgressError(message);
    }
    throw err;
  }
}

export function useListRoiReports(
  params: ListRoiReportsParams = {},
  enabled = true,
) {
  return useQuery({
    queryKey: ['pleri-roi-reports-list', params.month, params.limit],
    queryFn: () => fetchListRoiReports(params),
    enabled,
    staleTime: 1000 * 60 * 2,
  });
}

export function useRoiReportByMonth(
  month: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['pleri-roi-report', month],
    queryFn: () =>
      month ? fetchRoiReportByMonth(month) : Promise.resolve(null),
    enabled: enabled && !!month && /^\d{4}-\d{2}$/.test(month),
    staleTime: 1000 * 60 * 2,
  });
}

export function useGenerateRoiReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: generateRoiReport,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['pleri-roi-reports-list'] });
      if (variables && typeof variables.month === 'string' && variables.month) {
        queryClient.invalidateQueries({
          queryKey: ['pleri-roi-report', variables.month],
        });
      }
    },
  });
}
